export enum EitherTag {
  LEFT = 'left',
  RIGHT = 'right',
}

export interface Left<T> {
  readonly tag: EitherTag.LEFT;
  readonly value: T;
}

export interface Right<K> {
  readonly tag: EitherTag;
  readonly value: K;
}

export type Either<T, K> = Left<T> | Right<K>;

export type ExtractLeft<E> = E extends Left<infer L> ? L : never;
export type ExtractRight<E> = E extends Right<infer R> ? R : never;

export function left<T = never, K = never>(x: T): Either<T, K> {
  return {tag: EitherTag.LEFT, value: x};
}

export function right<T = never, K = never>(x: K): Either<T, K> {
  return {tag: EitherTag.RIGHT, value: x};
}

export function isLeft<T = never, K = never>(x: Either<T, K>): x is Left<T> {
  return x.tag === EitherTag.LEFT;
}

export function isRight<T = never, K = never>(x: Either<T, K>): x is Right<K> {
  return x.tag === EitherTag.RIGHT;
}

export function isEither<T = never, K = never>(x: unknown): x is Either<T, K> {
  //@ts-expect-error function interface is incompatible
  return isLeft(x) || isRight(x);
}

export function onLeft<T = never, K = never>(
  x: Either<T, K>,
  callback: (value: T) => unknown
): T | void {
  if (isLeft(x)) {
    callback(x.value);
    return x.value;
  }
}

export function onRight<T = never, K = never>(
  x: Either<T, K>,
  callback: (value: K) => unknown
): K | void {
  if (isRight(x)) {
    callback(x.value);
    return x.value;
  }
}

export function fold<TLeft, TRight, LeftResponse, RightResponse>(
  onLeft: (l: TLeft) => LeftResponse,
  onRight: (r: TRight) => RightResponse
): (x: Either<TLeft, TRight>) => LeftResponse | RightResponse {
  return (x: Either<TLeft, TRight>): LeftResponse | RightResponse => {
    return isLeft(x) ? onLeft(x.value) : onRight(x.value);
  };
}
